// Namespaced (`redo.partners.*`) so partner tokens never collide with
// merchant/marketplace tokens at shared parent domains, and so `clear()`
// only nukes our key — never anything written by another Redo property
// running in the same browser profile.
export const PARTNER_AUTH_TOKEN_KEY = "redo.partners.auth_token";

// Defensive shape check: a JWT we issued is three base64url segments
// joined by dots. Garbage in localStorage (older format, manual edit,
// quota corruption) gets treated as no-token rather than sent verbatim
// in `Authorization: Bearer <garbage>` — saves a wasted 401 round-trip
// on every app boot when the slot is corrupt.
const JWT_PATTERN = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

export function readAuthToken(
  storage: Pick<Storage, "getItem"> = localStorage,
): string | undefined {
  let raw: string | null;
  try {
    raw = storage.getItem(PARTNER_AUTH_TOKEN_KEY);
  } catch {
    // Reading from localStorage can throw in some browsers when the
    // origin is sandboxed (e.g. data: URLs in tests); treat the same
    // as absence.
    return undefined;
  }
  if (typeof raw !== "string" || raw.length === 0) {
    return undefined;
  }
  if (!JWT_PATTERN.test(raw)) {
    return undefined;
  }
  return raw;
}

export function writeAuthToken(
  token: string,
  storage: Pick<Storage, "setItem"> = localStorage,
): void {
  storage.setItem(PARTNER_AUTH_TOKEN_KEY, token);
}

export function clearAuthToken(
  storage: Pick<Storage, "removeItem"> = localStorage,
): void {
  storage.removeItem(PARTNER_AUTH_TOKEN_KEY);
}

// Subscribe to *cross-tab* mutations only. The `storage` event fires
// in tabs OTHER than the one that wrote the change — perfect for
// "logged out in tab 1, tab 2 still showing dashboard" sync. The
// listener filters to our key + `storageArea === localStorage` so a
// hostile sessionStorage edit can't impersonate a real token.
export function subscribeAuthTokenChanges(
  handler: (token: string | undefined) => void,
): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.key !== PARTNER_AUTH_TOKEN_KEY) {
      return;
    }
    if (event.storageArea !== localStorage) {
      return;
    }
    handler(readAuthToken());
  };
  window.addEventListener("storage", onStorage);
  return () => window.removeEventListener("storage", onStorage);
}
